import { useRef, useState } from 'react';
import { Alert, Divider, Button, notification, Flex } from 'antd';
import { QrcodeOutlined, PoweroffOutlined } from '@ant-design/icons';
import TwoFactorAuthForm from '../auth/TwoFactorAuthForm';
import { useAuthService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { notificationType } from '../../common/constants/notificationType';
import { buttonStyle } from '../../theming';
import { useTranslation } from 'react-i18next';
import { apiErrorText } from '../../utils/apiErrorText';
import { useReadOnly } from '../../hooks/useReadOnly';

// the card was out of date: 2FA was already turned on or off elsewhere
const STALE_STATE_CODES = ['QR_CODE_ALREADY_GENERATED', 'TWO_FACTOR_AUTH_NOT_ENABLED'];

export default function TwoFactorAuth() {
    const [api, contextHolder] = notification.useNotification();
    const { currentUser, fetchUser, setAuth } = useAuth();
    const { t } = useTranslation(['settings', 'apiResponses', 'impersonation']);
    // in an impersonation tab the card only shows the target's 2FA status; the API refuses every change anyway
    const readOnly = useReadOnly();
    const readOnlyNotice = readOnly && (
        <Alert type="info" showIcon message={t('impersonation:twoFactorReadOnly')} style={{ marginBottom: 16 }} />
    );

    const { getGeneratedQr, enableTwoFactorAuth, disableTwoFactorAuth } = useAuthService();
    const [loading, setLoading] = useState(false);
    const [qrCode, setQrCode] = useState(null);
    // every QR request rotates the secret, so only one may be in flight
    const [qrLoading, setQrLoading] = useState(false);
    const qrRequested = useRef(false);
    // turning 2FA off asks for a current code; the API refuses to disable without one
    const [isDisabling, setIsDisabling] = useState(false);

    // #region handlers
    function errorNotification(error) {
        api[notificationType.ERROR]({ ...apiErrorText(t, error), placement: 'bottomRight' });
        if (STALE_STATE_CODES.includes(error?.response?.data?.code)) {
            fetchUser();
        }
    }

    async function getQrCode() {
        if (qrRequested.current) {
            return;
        }
        qrRequested.current = true;
        setQrLoading(true);
        try {
            const { data } = await getGeneratedQr();

            if (data) {
                setQrCode(data);
            }
        } catch (error) {
            qrRequested.current = false;
            errorNotification(error);
        } finally {
            setQrLoading(false);
        }
    }

    async function turnOffTwoFactorAuth(form) {
        try {
            setLoading(true);
            const response = await disableTwoFactorAuth(form.code);

            if (response) {
                setIsDisabling(false);
                fetchUser();
            }

            setLoading(false);
        } catch (error) {
            errorNotification(error);
        } finally {
            setLoading(false);
        }
    }

    async function onFormSubmit(form) {
        try {
            setLoading(true);
            // the API signs out every other session and returns a 2FA-completed one for this device
            const { data } = await enableTwoFactorAuth(form.code);
            if (data) {
                setAuth(data.accessToken, data.refreshToken);
            }

            setLoading(false);
        } catch (error) {
            errorNotification(error);
        } finally {
            setLoading(false);
        }
    }
    // #endregion

    if (!currentUser.isTwoFactorEnable) {
        return (
            <Flex vertical justify="center">
                {contextHolder}
                {readOnlyNotice}
                <Divider orientation="left">{t('settings:2FA.step-1.title')}</Divider>
                <div style={{ margin: '0 50px' }}>
                    <p>{t('settings:2FA.step-1.subtitle')}</p>
                </div>
                <Divider orientation="left">{t('settings:2FA.step-2.title')}</Divider>
                <div style={{ margin: '0 50px' }}>
                    <p>{t('settings:2FA.step-2.subtitle')}</p>
                    <Button
                        type="primary"
                        icon={<QrcodeOutlined />}
                        onClick={getQrCode}
                        loading={qrLoading}
                        disabled={readOnly || Boolean(qrCode)}
                        style={{ ...buttonStyle.medium }}
                    >
                        {t('settings:2FA.step-2.button')}
                    </Button>
                    <div style={{ margin: '10px 0' }}>
                        {qrCode && <img src={qrCode} alt="Two-Factor Authentication QR Code" />}
                    </div>
                </div>

                <Divider orientation="left">{t('settings:2FA.step-3.title')}</Divider>
                <div style={{ margin: '0 50px' }}>
                    <p>{t('settings:2FA.step-3.subtitle')}</p>
                    <div style={{ maxWidth: 350 }}>
                        <TwoFactorAuthForm
                            loading={loading}
                            showCancel={false}
                            submitDisabled={readOnly || !qrCode}
                            onFormSubmit={onFormSubmit}
                            t={t}
                        />
                    </div>
                </div>
            </Flex>
        );
    }

    if (currentUser.isTwoFactorEnable) {
        return (
            <div>
                {contextHolder}
                {readOnlyNotice}
                <Divider orientation="left">{t('settings:2FA.turn-off.title')}</Divider>
                {isDisabling ? (
                    <div style={{ maxWidth: 350 }}>
                        <TwoFactorAuthForm
                            loading={loading}
                            showCancel={true}
                            submitDisabled={false}
                            onBackClick={() => setIsDisabling(false)}
                            onFormSubmit={turnOffTwoFactorAuth}
                            t={t}
                        />
                    </div>
                ) : (
                    <Button
                        type="primary"
                        icon={<PoweroffOutlined />}
                        onClick={() => setIsDisabling(true)}
                        loading={loading}
                        disabled={readOnly}
                        danger
                        style={{ ...buttonStyle.medium }}
                    >
                        {t('settings:2FA.turn-off.button')}
                    </Button>
                )}
            </div>
        );
    }
}
